import { useState, FormEvent, useCallback } from "react";
import Navbar from "../../components/layout/Navbar";
import { Link } from "react-router-dom";
import { Button } from "../../components/Button";
import {
  useCreateNewSiteMutation,
  useLazyCheckSiteQuery,
} from "../../services/generalApi";
import { useSelector } from "react-redux";
import { selectToken } from "../../features/authSlice";
import { debounce } from "../../utils/debounce";
import Cookies from "js-cookie";
import { toast } from "sonner";

const StoreSetup = () => {
  const token = useSelector(selectToken);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
  });

  const [error, setError] = useState("");
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);

  const [createNewSite, { isLoading: isCreating }] = useCreateNewSiteMutation();
  const [checkSite, { isLoading: isChecking }] = useLazyCheckSiteQuery();

  // Create a debounced version of the check availability function
  const debouncedCheckAvailability = useCallback(
    debounce((slug: string) => {
      if (slug) {
        checkAvailability(slug);
      } else {
        setSlugAvailable(null);
        setError("");
      }
    }, 500) as (slug: string) => void,
    [token]
  );

  // Function to check slug availability
  const checkAvailability = async (slug: string) => {
    if (!slug) {
      setSlugAvailable(null);
      return;
    }

    try {
      const result = await checkSite({
        slug,
        access: token || "",
      }).unwrap();

      setSlugAvailable(result.available);
      if (!result.available) {
        setError("This domain is already taken. Please try another one.");
      } else {
        setError("");
      }
    } catch (err) {
      console.error("Failed to check domain availability:", err);
      setError("Error checking domain availability. Please try again.");
      setSlugAvailable(null);
    }
  };

  const validateSlug = (slug: string) => {
    const slugRegex = /^[-a-zA-Z0-9_]+$/;
    return slugRegex.test(slug);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Reset availability status when editing the slug
    if (name === "slug") {
      if (value !== "" && !validateSlug(value)) {
        return;
      }
      setSlugAvailable(null);
      // Trigger debounced check if the slug has at least 2 characters
      if (value.length >= 2 && validateSlug(value)) {
        debouncedCheckAvailability(value);
      } else {
        setSlugAvailable(null);
        setError("");
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // // Manual check availability function (for the button)
  // const handleCheckAvailability = () => {
  //   if (!formData.slug) {
  //     setError("Please enter a store domain");
  //     return;
  //   }

  //   checkAvailability(formData.slug);
  // };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.slug) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      // Check slug availability again before submitting
      const availabilityCheck = await checkSite({
        slug: formData.slug,
        access: token || "",
      }).unwrap();

      if (!availabilityCheck.available) {
        setError("This domain is already taken. Please try another one.");
        return;
      }
      await createNewSite({
        body: {
          name: formData.name,
          slug: formData.slug,
          site_type: "ecommerce",
        },
        access: token || "",
      }).unwrap();

      toast.success("Store created successfully!", {
        description: "Redirecting to your store...",
      });

      window.location.href = `${
        import.meta.env.VITE_DASHBOARD_URL
      }/setup?slug=${formData.slug}`;
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null && "data" in error) {
        const err = error as { data?: Record<string, string[]> };
        toast.error(
          Object.entries(err.data as Record<string, string[]>)
            .map(([_, value]) => value)
            .join(", ") || "Failed to create store"
        );
      }
      console.error("Failed to create store:", error);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 h-[81vh] sm:p-14 flex items-start md:items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-between gap-20 ">
            <div className="flex flex-col gap-6 items-start justify-start w-[500px]">
              <h2 className="">Create your store</h2>
              {error && <p className="text-red-500">{error}</p>}
              <form
                className="flex flex-col items-start justify-center gap-5 w-full"
                onSubmit={handleSubmit}
              >
                <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                  <label htmlFor="name" className="text-[#666666CC]">
                    Store Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    placeholder="e.g Ella's Collections"
                    value={formData.name}
                    onChange={handleChange}
                    className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                    required
                  />
                </div>
                <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                  <label htmlFor="slug" className="text-[#666666CC]">
                    Store Domain (Website name)
                  </label>
                  <div className="relative w-full">
                    {" "}
                    <div
                      className={`h-11 w-full border border-[#66666659] rounded-lg outline-0 pr-3 pl-1 flex items-center justify-between ${
                        formData.slug.length >= 2
                          ? slugAvailable === true
                            ? "border-green-500"
                            : slugAvailable === false
                            ? "border-red-500"
                            : ""
                          : ""
                      }`}
                    >
                      <input
                        type="text"
                        name="slug"
                        id="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        className="w-full outline-0 h-full text-lg"
                        placeholder="shopwithella"
                        required
                      />
                      <span className="text-lg">.jellosite.store</span>
                    </div>
                    <div className="flex items-center text-sm mt-1 h-6">
                      {isChecking && (
                        <span className="text-gray-500">
                          Checking availability...
                        </span>
                      )}
                      {!isChecking && slugAvailable === true && (
                        <span className="text-green-500 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Slug available
                        </span>
                      )}
                      {!isChecking && slugAvailable === false && (
                        <span className="text-red-500 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Slug not available
                        </span>
                      )}
                    </div>
                  </div>
                  {/* <button
                    type="button"
                    onClick={handleCheckAvailability}
                    className="text-[#7E57C2] text-sm mt-2"
                    disabled={isChecking || formData.slug.length === 0}
                  >
                    Check manually
                  </button> */}
                </div>
                <Button
                  text={isCreating ? "Creating Store..." : "Create Store"}
                  disabled={isCreating || slugAvailable !== true}
                  className="w-full sm:w-fit"
                />
              </form>
            </div>
            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%]  items-center justify-end mt-1 ">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="">
              <li>Privacy</li>
            </Link>
            <Link to="">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StoreSetup;
